// Mobile bottom tab bar — delegates to @synvya/portal-ui/mobile.
// Account-specific: uses react-router NavLink, i18n, and local MoreSheet.

import { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeftRight, MoreHorizontal, LogOut } from 'lucide-react'
import { MobileNav as MobileNavShell, MoreSheet } from '@synvya/portal-ui/mobile'
import type { TabConfig } from '@synvya/portal-ui/mobile'
import { groups } from './navRegistry'
import { useAuth } from '@/state/useAuth'
import { useTeam } from '@/state/useTeam'
import { useCanSwitchRestaurant } from '@/hooks/useCanSwitchRestaurant'
import { SwitchRestaurantDialog } from '@/components/SwitchRestaurantDialog'

function TabButton({ tab, active }: { tab: TabConfig; active: boolean }) {
  const { t } = useTranslation()
  return (
    <NavLink
      to={tab.to}
      className={`flex w-full flex-col items-center justify-center gap-0.5 py-2 text-xs ${
        active
          ? 'font-semibold text-primary'
          : 'text-muted-foreground'
      }`}
    >
      <tab.Icon className="h-5 w-5" />
      <span>{t(`common:${tab.label}`)}</span>
    </NavLink>
  )
}

export function MobileNav() {
  const [moreOpen, setMoreOpen] = useState(false)
  const [switching, setSwitching] = useState(false)
  const canSwitch = useCanSwitchRestaurant()
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { reset } = useTeam()

  return (
    <>
      <MobileNavShell
        groups={groups}
        pathname={location.pathname}
        renderTab={(tab, active) => <TabButton tab={tab} active={active} />}
        renderMoreButton={() => (
          <button
            onClick={() => setMoreOpen(true)}
            className={`flex w-full flex-col items-center justify-center gap-0.5 py-2 text-xs ${
              moreOpen ? 'font-semibold text-primary' : 'text-muted-foreground'
            }`}
            aria-label={t('common:nav.more')}
          >
            <MoreHorizontal className="h-5 w-5" />
            <span>{t('common:nav.more')}</span>
          </button>
        )}
      />
      <MoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        groups={groups}
        onNavigate={(to) => navigate(to)}
        renderGroupLabel={(label) => t(`common:${label}`)}
        renderItemLabel={(label) => t(`common:${label}`)}
        ariaLabel={t('common:nav.more')}
        footer={
          <div className="border-t border-border pt-3">
            {canSwitch && (
              <button
                onClick={() => {
                  setMoreOpen(false)
                  setSwitching(true)
                }}
                className="flex w-full items-center gap-3 rounded-lg px-2 py-3 text-sm text-foreground hover:bg-accent"
              >
                <ArrowLeftRight className="h-5 w-5" />
                <span>Switch account</span>
              </button>
            )}
            <button
              onClick={async () => {
                setMoreOpen(false)
                await logout()
                reset()
                navigate('/sign-in')
              }}
              className="flex w-full items-center gap-3 rounded-lg px-2 py-3 text-sm text-destructive hover:bg-destructive/10"
            >
              <LogOut className="h-5 w-5" />
              <span>{t('common:nav.signOut')}</span>
            </button>
          </div>
        }
      />
      <SwitchRestaurantDialog open={switching} onClose={() => setSwitching(false)} />
    </>
  )
}
